import { ReactElement, useCallback } from "react";
import { HubCommentSection, HubCommentSectionService } from "../HubCommentSection/HubCommentSection";
import { useCommentSection } from "../HubCommentSection/hooks/useCommentSection";
import { HubCommentModel, HubCommentPagingInformationModel } from "client/Api/Hub/common";
import { SortDirection } from "client/Api/common";
import { MobileComment } from "../HubCommentSection/MobileComment/MobileComment";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { TimeZone } from "client/DateAndTime/timezones";
import { BusyIndicator, NoResults } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CommunityPostDialogCommentsSection.module.less";
import { communityCommentsSortKey } from "./CommunityPostPageCommentSection";

export type CommunityPostDialogCommentSectionProps = {
	onPostStatisticsUpdate: () => void;
	onMobileCommentIsEditingChanged: (isEditing: boolean) => void;
	options: HubCommentOptionsModel;
	timezone: TimeZone;
	commentsCount: number;
	service: HubCommentSectionService;
};

export const CommunityPostDialogCommentSection = (
	props: CommunityPostDialogCommentSectionProps
): ReactElement => {
	const {
		onPostStatisticsUpdate,
		onMobileCommentIsEditingChanged,
		options,
		timezone,
		commentsCount,
		service
	} = props;

	const getCommentsListHandler = useCallback(
		(
			pageSize: number,
			after: HubCommentPagingInformationModel | null,
			sortDirection: SortDirection,
			signal: AbortSignal
		): Promise<LazyLoadPageResult<HubCommentModel>> => {
			return service.getCommentsList(
				pageSize,
				after,
				sortDirection,
				signal
			);
		},
		[service]
	);

	const {
		commentListRef,
		currentUserInfo,
		imageViewerDialog,
		isMobile,
		mobileCommentFormRef,
		state: commentState,
		structuredComments,
		mobileMode: mobileCommentMode,
		sortPreference: commentSortPreference,
		loadMoreHandler: loadMoreCommentsHandler,
		initMobileMode: initMobileCommentMode,
		mobileModeChangeHandler: mobileCommentModeChangeHandler,
		onSortPreferenceChange: onCommentSortPreferenceChange,
		commentDeletedHandler,
		commentReactionHandler,
		commentCreatedHandler,
		commentUpdatedHandler,
		viewImageHandler,
		reloadHandler
	} = useCommentSection({
		getCommentsList: getCommentsListHandler,
		sortKey: communityCommentsSortKey,
		onStatisticsUpdate: onPostStatisticsUpdate
	});

	const hasComments: boolean = structuredComments.length > 0;

	if (commentState.status === "loading" && !hasComments) {
		return (
			<div className={styles.busyIndicatorContainer}>
				<BusyIndicator />
			</div>
		);
	}

	if (commentState.status === "error" && !hasComments) {
		return (
			<NoResults
				icon="gear"
				title={localize("_NotAbleToRetrieveComments")}
				message={localize("_TheCommentsCouldNotBeRetrieved")}
				onRetry={reloadHandler}
			/>
		);
	}

	return (
		<>
			<HubCommentSection>
				{!isMobile && (
					<HubCommentSection.CommentForm
						service={service}
						onCreated={commentCreatedHandler}
						currentUserInfo={currentUserInfo}
						options={options}
					/>
				)}
				{hasComments && (
					<>
						<HubCommentSection.Header
							total={commentsCount}
							sortPreference={commentSortPreference}
							onSortPreferenceChange={onCommentSortPreferenceChange}
						/>
						<HubCommentSection.CommentList
							ref={commentListRef}
							comments={structuredComments}
							onStatisticsUpdated={commentReactionHandler}
							onUpdated={commentUpdatedHandler}
							onDeleted={commentDeletedHandler}
							onCreated={commentCreatedHandler}
							isMobile={isMobile}
							mobileModeChangeHandler={mobileCommentModeChangeHandler}
							mobileMode={mobileCommentMode}
							service={service}
							currentUserInfo={currentUserInfo}
							timezone={timezone}
							viewImageHandler={viewImageHandler}
							commentOptions={options}
							renderAfterItems={
								commentState.status === "error"
									? (
										<NoResults
											icon="gear"
											title={localize("_NotAbleToRetrieveComments")}
											message={localize("_TheCommentsCouldNotBeRetrieved")}
											onRetry={loadMoreCommentsHandler}
										/>
									)
									: (
										<HubCommentSection.LoadMore
											variant="load-more-indicator"
											hasMore={commentState.hasMore}
											isLoading={commentState.status === "loading"}
											onLoadMore={loadMoreCommentsHandler}
										/>
									)
							}
						/>
					</>
				)}
				{isMobile && (
					<MobileComment
						currentUserInfo={currentUserInfo}
						onCreated={commentCreatedHandler}
						mode={mobileCommentMode}
						onCancel={initMobileCommentMode}
						onUpdated={commentUpdatedHandler}
						ref={mobileCommentFormRef}
						onMobileCommentIsEditingChanged={onMobileCommentIsEditingChanged}
						service={service}
						options={options}
					/>
				)}
			</HubCommentSection>
			{imageViewerDialog}
		</>
	);
};
CommunityPostDialogCommentSection.displayName = "CommunityPostDialogCommentSection";
